import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoEyeOutline, IoFilterOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { toast } from 'react-toastify';
import { usePendingCompOffRequests } from '../../context/PendingCompOffRequestContext';
import api from '../../utils/api';
import { formatDate, formatTiming } from '../../utils/format';

function ManagerCompOffRequests() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const [compOffRequests, setCompOffRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectComment, setRejectComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiLoading, setApiLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  
  // Bulk action states
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectComment, setBulkRejectComment] = useState('');
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  
  const requestsPerPage = 6;
  const baseUrl = process.env.REACT_APP_API_URL;
  const { updatePendingCompOffCount } = usePendingCompOffRequests();

  const fetchCompOffRequests = async () => {
    if (!currentUser?.id) return;
    setApiLoading(true);
    try {
      const response = await api.get(`/api/compoffs/recent?managerId=${currentUser?.id}`);
      const formattedData = response.data.map(req => ({
        id: req.id,
        employeeName: req.employeeName,
        employeeId: req.employeeId,
        compOffDate: req.onDate,
        reason: req.reason,
        timing: req.timing,
        status: req.comOffStatus,
        approverComment: req.approverComment
      }));
      setCompOffRequests(formattedData);
    } catch (error) {
      console.error('Failed to fetch comp off requests:', error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetchCompOffRequests();
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!loading) {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      if (currentUser.role !== 'MANAGER') {
        navigate('/unauthorized');
        return;
      }
    }
  }, [currentUser, loading, navigate]);

  const filteredRequests = compOffRequests.filter(request => 
    statusFilter === 'all' ? true : request.status?.toLowerCase() === statusFilter.toLowerCase()
  );

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );

  const handleView = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  const fetchPendingCount = async () => {
    try {
      const response = await api.get(
        `/api/compoffs/pending-count?managerId=${currentUser?.id}`
      );
      updatePendingCompOffCount(response.data);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  const handleApprove = async (requestId) => {
    if (!currentUser?.id) return;
    setIsApproving(true);
    try {
      await api.put(`/api/compoffs/approve/${requestId}`, null, {
        params: { 
          managerId: currentUser?.id,
          comment: '' 
        }
      });
      await fetchPendingCount();
      await fetchCompOffRequests();
      setShowViewModal(false);
      toast.success('Comp off request approved successfully');
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Approval failed');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!currentUser?.id || !rejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsRejecting(true);
    try {
      await api.put(`/api/compoffs/reject/${requestId}`, null, {
        params: {
          managerId: currentUser?.id,
          comment: rejectComment
        }
      });
      await fetchPendingCount();
      await fetchCompOffRequests();
      setShowViewModal(false);
      setRejectComment('');
      toast.success('Comp off request rejected successfully');
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Rejection failed');
    } finally {
      setIsRejecting(false);
    }
  };

  // Bulk action handlers
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

 const handleSelectAll = () => {
  const currentPageIds = paginatedRequests.map(req => req.id);
  const allSelected = currentPageIds.every(id => selectedRequests.includes(id));

  if (allSelected) {
    // Unselect all on current page
    setSelectedRequests(prev => prev.filter(id => !currentPageIds.includes(id)));
  } else {
    // Select all on current page (keep previous selections)
    setSelectedRequests(prev => [...new Set([...prev, ...currentPageIds])]);
  }
};


  const handleBulkApprove = async () => {
    if (!currentUser?.id || selectedRequests.length === 0) return;
    
    setIsBulkApproving(true);
    try {
      const results = await Promise.allSettled(
        selectedRequests.map(requestId =>
          api.put(`/api/compoffs/approve/${requestId}`, null, {
            params: {
              managerId: currentUser?.id,
              comment: ''
            }
          })
        )
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast.success(`${successCount} request(s) approved successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} request(s) failed to approve`);
      }

      await fetchPendingCount();
      await fetchCompOffRequests();
      setSelectedRequests([]);
    } catch (error) {
      console.error('Bulk approve failed:', error);
      toast.error('Bulk approval failed');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (!currentUser?.id || selectedRequests.length === 0 || !bulkRejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsBulkRejecting(true);
    try {
      const results = await Promise.allSettled(
        selectedRequests.map(requestId =>
          api.put(`/api/compoffs/reject/${requestId}`, null, {
            params: {
              managerId: currentUser?.id,
              comment: bulkRejectComment
            }
          })
        )
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast.success(`${successCount} request(s) rejected successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} request(s) failed to reject`);
      }

      await fetchPendingCount();
      await fetchCompOffRequests();
      setSelectedRequests([]);
      setShowBulkRejectModal(false);
      setBulkRejectComment('');
    } catch (error) {
      console.error('Bulk reject failed:', error);
      toast.error('Bulk rejection failed');
    } finally {
      setIsBulkRejecting(false);
    }
  };

  const isActionInProgress = isApproving || isRejecting;
  const isBulkActionInProgress = isBulkApproving || isBulkRejecting;

  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="flex justify-center items-center h-[400px]">
          <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div 
        className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h2 className="text-xl xs:text-2xl font-bold">Team Comp Off Requests</h2>
        <div className="flex items-center gap-2">
          <IoFilterOutline className="text-slate-600" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSelectedRequests([]);
            }}
            className="bg-white border border-slate-200 text-slate-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </motion.div>

      {/* Bulk Action Bar */}
      {selectedRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex justify-between items-center"
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-blue-800">
              {selectedRequests.length} request(s) selected
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleBulkApprove}
              disabled={isBulkActionInProgress}
              className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 ${
                isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {isBulkApproving ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Approving...</span>
                </>
              ) : (
                <>
                  <IoCheckmarkCircleOutline className="text-lg" />
                  <span>Approve All</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowBulkRejectModal(true)}
              disabled={isBulkActionInProgress}
              className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 ${
                isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              <IoCloseCircleOutline className="text-lg" />
              <span>Reject All</span>
            </button>
            <button
              onClick={() => setSelectedRequests([])}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </motion.div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Reject Multiple Requests</h3>
              <button
                onClick={() => {
                  setShowBulkRejectModal(false);
                  setBulkRejectComment('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You are about to reject <strong>{selectedRequests.length}</strong> request(s). 
                Please provide a reason for rejection.
              </p>
              <textarea
                placeholder="Enter reason for rejection..."
                value={bulkRejectComment}
                onChange={(e) => setBulkRejectComment(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg text-sm"
                rows={4}
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkRejectModal(false);
                    setBulkRejectComment('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                  disabled={isBulkRejecting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={isBulkRejecting || !bulkRejectComment.trim()}
                  className={`px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${
                    isBulkRejecting || !bulkRejectComment.trim() ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isBulkRejecting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    "Reject All"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ pointerEvents: isActionInProgress ? 'none' : 'auto' }}
        >
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Comp Off Request Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Employee Name</p>
                  <p className="font-medium text-slate-800">{selectedRequest.employeeName}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Comp Off Date</p>
                  <p className="font-medium text-slate-800">{selectedRequest.compOffDate}</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800">{selectedRequest.reason}</p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Note:</span>
                  <span className="italic">Comment is required only for rejection</span>
                </div>
                <textarea
                  placeholder="Enter reason for rejection..."
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-lg text-sm"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                {selectedRequest.status && selectedRequest.status.toLowerCase() === "pending" && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={isApproving || selectedRequest.status.toLowerCase() !== "pending"}
                      className={`px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 flex items-center gap-2 ${
                        isApproving || selectedRequest.status.toLowerCase() !== "pending" ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {isApproving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Approving...</span>
                        </>
                      ) : (
                        "Approve"
                      )}
                    </button>
                    <button
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={isRejecting || selectedRequest.status.toLowerCase() !== "pending"}
                      className={`px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-2 ${
                        isRejecting || selectedRequest.status.toLowerCase() !== "pending" ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        "Reject"
                      )}
                    </button>
                  </>
                )}
                {selectedRequest.status && selectedRequest.status.toLowerCase() === "approved" && (
                  <>
                    <button
                      disabled
                      className="px-4 py-2 text-sm font-medium text-green-300 cursor-not-allowed"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={isRejecting}
                      className={`px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-2 ${
                        isRejecting ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        "Reject"
                      )}
                    </button>
                  </>
                )}
                {selectedRequest.status && selectedRequest.status.toLowerCase() === "rejected" && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={isApproving}
                      className={`px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 flex items-center gap-2 ${
                        isApproving ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {isApproving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Reapproving...</span>
                        </>
                      ) : (
                        "Reapprove"
                      )}
                    </button>
                    <button
                      disabled
                      className="px-4 py-2 text-sm font-medium text-red-300 cursor-not-allowed"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Desktop View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100 text-left text-slate-600">
              <tr>
                <th className="px-6 py-4 font-semibold">
                  <input
                    type="checkbox"
                    checked={
  paginatedRequests.length > 0 &&
  paginatedRequests.every(req => selectedRequests.includes(req.id))
}

                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-4 font-semibold">SL No</th>
                <th className="px-6 py-4 font-semibold">Employee</th>
                <th className="px-6 py-4 font-semibold">Comp Off Date</th>
                <th className="px-6 py-4 font-semibold">Timing</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-slate-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : paginatedRequests.length > 0 ? (
                paginatedRequests.map((request, index) => (
                  <tr key={request.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRequests.includes(request.id)}
                        onChange={() => handleSelectRequest(request.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {(currentPage - 1) * requestsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{request.employeeName}</td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(request.compOffDate)}</td>
                    <td className="px-6 py-4 text-slate-600">{formatTiming(request.timing)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        request.status && request.status.toLowerCase() === "approved"
                          ? "bg-green-100 text-green-800"
                          : request.status && request.status.toLowerCase() === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1).toLowerCase() : ""}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <motion.button
                          onClick={() => handleView(request)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="View Details"
                        >
                          <IoEyeOutline className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                    No comp off requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => {
                setCurrentPage(page);
              }}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* Mobile View */}
      <div className="sm:hidden space-y-4">
        {paginatedRequests.map((request, index) => (
          <div
            key={request.id}
            className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedRequests.includes(request.id)}
                    onChange={() => handleSelectRequest(request.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-500">#{(currentPage - 1) * requestsPerPage + index + 1}</span>
                  <h3 className="font-semibold text-slate-800">{request.employeeName}</h3>
                </div>
                <button
                  onClick={() => handleView(request)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IoEyeOutline className="text-xl" />
                  </motion.div>
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Comp Off Date:</span>
                  <span className="text-slate-700">{request.compOffDate}</span>
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-500">Reason:</span>
                  <span className="text-slate-700">{request.reason}</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    request.status && request.status.toLowerCase() === "approved"
                      ? "bg-green-100 text-green-800"
                      : request.status && request.status.toLowerCase() === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}>
                    {request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1).toLowerCase() : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="sm:hidden mt-6 flex justify-center">
        <Stack spacing={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => {
              setCurrentPage(page);
            }}
            variant="outlined"
            shape="rounded-full"
            size="medium"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#475569",
                borderColor: "#e2e8f0",
              },
              "& .Mui-selected": {
                backgroundColor: "#475569 !important",
                color: "#fff",
              },
            }}
          />
        </Stack>
      </div>
    </div>
  );
}

export default ManagerCompOffRequests;